import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

// Default button styles (all include disabled:opacity-50)
const prevButtonClass =
    'disabled:opacity-50 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded';
const nextButtonClass =
    'disabled:opacity-50 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded';
const submitButtonClassName =
    'disabled:opacity-50 bg-customGreen text-white font-bold py-2 px-4 rounded';

/**
 * Generic navigation component within a multi-page form.
 * Handles Previous/Next/Submit buttons and optional page indicator
 *
 * Note: Next/Submit buttons use type="submit" to trigger form validation.
 * The parent form's onSubmit handler controls the navigation logic.
 *
 * @example
 * <MultiPageFormNavigation
 *   currentPage={currentPage}
 *   totalPages={formPagesCount}
 *   showPageIndicator={true}
 *   onPrev={handlePrevPage}
 *   disableButtons={true}
 * />
 */
const MultiPageFormNavigation = ({
    currentPage,
    totalPages,
    showPageIndicator = false,
    onPrev,
    disableButtons = false,
}) => {
    const isFirstPage = _.isEqual(currentPage, 1);
    const isLastPage = _.isEqual(currentPage, totalPages);

    return (
        <>
            {/* Navigation Buttons Container */}
            <div
                className={`mt-12 mb-4 ${
                    isFirstPage
                        ? 'text-center'
                        : 'flex justify-between items-center'
                }`}
            >
                {/* Previous Button - only show if not on first page */}
                {!isFirstPage && (
                    <button
                        className={prevButtonClass}
                        type="button"
                        onClick={onPrev}
                        disabled={disableButtons}
                        data-testid="previous-page"
                    >
                        Previous Page
                    </button>
                )}

                {/* Next/Submit Button - type="submit" triggers form validation */}
                <button
                    className={
                        isLastPage ? submitButtonClassName : nextButtonClass
                    }
                    type="submit"
                    disabled={disableButtons}
                    data-testid={isLastPage ? 'submit-form' : 'next-page'}
                >
                    {isLastPage ? 'Submit Invoice' : 'Next Page'}
                </button>
            </div>

            {/* Page Indicator Footer - only show if multiple pages */}
            {showPageIndicator && totalPages > 1 && (
                <div className="clear-both block text-center mt-2">
                    Page {currentPage} of {totalPages}
                </div>
            )}
        </>
    );
};

MultiPageFormNavigation.propTypes = {
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    showPageIndicator: PropTypes.bool,
    onPrev: PropTypes.func.isRequired,
    disableButtons: PropTypes.bool,
};

export default MultiPageFormNavigation;
